// Import Packages

import React, { useState } from "react";
import ReactDataSheet from "react-datasheet";
import { useSetRecoilState } from 'recoil';
import { useTranslation } from "react-i18next";

// Import Material UI
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import { Button, Dialog, DialogContent, IconButton } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";

// Import Pages
import { TabPanel } from '../../components/FlowConfigurationInputs/index'
import { alertState } from '../../recoil-store/recoil-state';
import close from "../../assets/icons/close.svg";

// Import Helpers
import { getExtension } from '../../helpers/helper';

// Import CSS
import "react-datasheet/lib/react-datasheet.css";



export default function DataTableVariableEditor({
  data,
  setData,
}) {
  const { t } = useTranslation();
  const label = "CONTENT.CONFIGURATION-FORM.DATA-TABLE.ASSIGN.";
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('manuel');

  const [open, setOpen] = useState(false);
  const setAlert = useSetRecoilState(alertState);


  // Handle cell value changes
  const onCellsChanged = (changes) => {
    const grid = data.map((row) => [...row]);
    changes.forEach(({ cell, row, col, value }) => {
      grid[row][col] = value;
    });
    // Update state
    setData(grid);
  };
  const handleDialogClick = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setSelected(null);
    setOpen(false);
  };
  // Handle adding new row
  const handleAddRow = (e) => {
    let newRow = [];
    if (data[0]?.length) {
      // Push column values
      for (let i of data[0]) {
        newRow.push("");
      }
    } else {
      newRow.push("");
    }
    data.push(newRow);
    // Add new row
    setData([...data]);
    setSelected(null);
  };
  // Handle adding new column
  const handleAddColumn = (e) => {
    for (let item of data) {
      // Push row values
      item.push("");
    }
    setData([...data]);
    setSelected(null);
  };
  // Handle removing row
  const handleRemoveRow = (e) => {
    // Remove row
    if (selected) {
      if (selected.end.i < selected.start.i) {
        if (selected.end.i === 0) {
          if (selected.end.i === selected.start.i) {
            return;
          }
          selected.end.i += 1;
        }
        data.splice(selected.end.i, selected.start.i - selected.end.i + 1);
      } else {
        if (selected.start.i === 0) {
          if (selected.end.i === selected.start.i) {
            return;
          }
          selected.start.i += 1;
        }
        data.splice(selected.start.i, selected.end.i - selected.start.i + 1);
      }
    }
    if (data.length === 0) {
      setData([[""]]);
    } else {
      setData([...data]);
    }
    setSelected(null);
  };
  // Handle removing column
  const handleRemoveColumn = (e) => {
    if (selected) {
      for (let item of data) {
        if (selected.end.j < selected.start.j) {
          item.splice(selected.end.j, selected.start.j - selected.end.j + 1);
        } else {
          item.splice(
            selected.start.j,
            selected.end.j - selected.start.j + 1
          );
        }
      }
    }
    if (data[0].length === 0) {
      setData([[""]]);
    } else {
      setData([...data]);
    }
    setSelected(null);
  };
  const handleSelect = (index) => {
    setSelected(index);
  };

  const getSelected = type => {
    if (selected.end[type] < selected.start[type]) {
      return selected.start[type] - selected.end[type] + 1;
    } else {
      return selected.end[type] - selected.start[type] + 1;
    }
  }

  const handleFileChange = (event) => {
    if (event.target.files[0]?.name) {
      let extension = getExtension(event.target.files[0]?.name);
      if (extension === 'xlsx') {
        setData(d => ({ ...d, file: event.target.files[0] }));
      } else {
        setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.LICENSE.LICENSE-XML-FORMAT'), severity: 'error' }))
      }
    }
  };

  const changeSheetName = (event) => {
    setData(d => ({ ...d, sheetName: event.target.value }));
  };

  function addLicense() {
    document.getElementById("excel-file-input").click();
  }

  const changeTab = (v) => {
    if (v === 'manuel') {
      setData([
        ['', ''], ['', '']
      ]);
    } else {
      setData(undefined);
    }
    setTab(v)
  }

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex' }}>
          <div className="condition-tabs form-element">
            <button className={`col-4 ${tab === 'manuel' ? 'condition-active' : ''}`}
              value="manuel" onClick={() => changeTab('manuel')}>
              Manuel
            </button>
            <button className={`col-4 ${tab === 'browse' ? 'condition-active' : ''}`}
              value="browse" onClick={() => changeTab('browse')}>
              Browse
            </button>
          </div>
        </div>
        <TabPanel value={tab} index={'manuel'}>
          <div style={{ display: "flex" }}>
            <ReactDataSheet
              data={data}
              valueRenderer={(cell) => cell}
              onCellsChanged={onCellsChanged}
            />

            <Button onClick={handleDialogClick} style={{ height: 30, marginTop: "-8px" }}>
              <FullscreenIcon />
            </Button>
          </div>

          <Dialog open={open} onClose={handleClose} maxWidth="lg">
            <DialogContent style={{ textAlign: "center" }}>
              <Button
                variant="outlined"
                onClick={handleAddRow}
                style={{ marginRight: 5, color: "#43AA41" }}
              >
                {t(label + "ADD-ROW")}
              </Button>
              <Button
                variant="outlined"
                onClick={handleAddColumn}
                style={{ marginRight: 5, color: "#43AA41" }}
              >
                {t(label + "ADD-COLUMN")}
              </Button>
              <Button
                variant="outlined"
                onClick={handleRemoveRow}
                disabled={!selected}
                style={{ marginRight: 5, color: !selected ? "#F5C6C6" : "#D75E5E" }}
              >
                {t(label + "REMOVE-ROW", { count: selected ? getSelected('i') : '' })}
              </Button>
              <Button
                variant="outlined"
                onClick={handleRemoveColumn}
                disabled={!selected}
                style={{ color: !selected ? "#F5C6C6" : "#D75E5E" }}
              >
                {t(label + "REMOVE-COLUMN", { count: selected ? getSelected('j') : '' })}
              </Button>
              <IconButton style={{ float: "right" }} onClick={handleClose}>
                <img src={close} alt="close-icon" />
              </IconButton>
            </DialogContent>
            <DialogContent>
              <ReactDataSheet
                data={data}
                valueRenderer={(cell) => cell}
                onSelect={handleSelect}
                onCellsChanged={onCellsChanged}
              />
            </DialogContent>
          </Dialog>
        </TabPanel>
        <TabPanel value={tab} index={'browse'}>

          <div className="mail-server-bottom-section">
            <input type="file" name="file" accept=".xlsx" hidden id="excel-file-input"
              onChange={handleFileChange} />
            <div className="mail-server-button-save" onClick={addLicense}>
              {t(label + "UPLOAD-PREVIEW-FILE")}
            </div>
            {
              data && data.file && data.file.name &&
              <TextField
                placeholder="Sheet Name"
                size="small"
                style={{ width: '22%', marginLeft: '12px' }}
                id="outlined-basic"
                variant="outlined"
                value={data && data.sheetName ? data.sheetName : ''}
                onChange={changeSheetName}
                inputProps={{ style: { fontSize: 12 } }}
              />
            }

          </div>
          <small className="mail-server-small-text">{data && data.file && data.file.name}</small>

        </TabPanel>

      </div>
    </div>
  );
}
